<script setup>
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import { useThemeConfig } from '@core/composable/useThemeConfig'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import authV2ForgotPasswordIllustrationLight from '@/assets/images/pages/auth-v2-forgot-password-illustration-light-new.jpg'
import authV2MaskDark from '@/assets/images/pages/auth-v2-mask-dark.png'
import authV2MaskLight from '@/assets/images/pages/auth-v2-mask-light.png'
import axios from "@axios"
import { useRoute } from 'vue-router'
import {useAppAbility} from "@/plugins/casl/useAppAbility"

import {
  requiredValidator
} from '@validators'
import { safeRedirectPath } from '@/utils/safeRedirect'

const ability = useAppAbility()
const {isAppRtl} = useThemeConfig()
const refVForm = ref()
const password = ref('')
const submitted = ref(false)
const isPasswordVisible = ref(false)
const authThemeImg = useGenerateImageVariant(authV2ForgotPasswordIllustrationLight, true)
const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)
const errors = ref({
  password: undefined
})
const router = useRouter()

const route = useRoute()

// A set-password link works once (11/2): setting the password clears its
// code, and a newer reset link replaces it. The page asks the server when it
// opens, so a used link says so instead of showing a form that cannot work.
// 'checking' → 'ready' (the form) or 'used' (the server's message).
const linkState = ref('checking')
const usedMessage = ref('')

const showUsed = message => {
  usedMessage.value = message
  linkState.value = 'used'
}

onMounted(() => {
  axios.post('/auth/set-password/check', { passwordResetCode: route.query.passwordResetCode || '' })
    .then(r => {
      if (r.data && r.data.usable === false) showUsed(r.data.message)
      else linkState.value = 'ready'
    })
    // Can't tell (offline, server error): show the form; a submit still says why it failed.
    .catch(() => { linkState.value = 'ready' })
})

const login = () => {
  axios.post('/auth/set-password', {
    password: password.value,
    passwordResetCode: route.query.passwordResetCode
  }).then(r => {
    submitted.value = true
    const {userData, userAbilities, jwt} = r.data
    localStorage.setItem('userAbilities', JSON.stringify(userAbilities))
    ability.update(userAbilities)
    localStorage.setItem('userData', JSON.stringify(userData))
    localStorage.setItem('accessToken', JSON.stringify(jwt))
    localStorage.setItem('jwt', JSON.stringify(jwt))
    // RM-223: the member just activated — they are no longer a guest. Clear the
    // "resume joining" funnel value so a later /home visit (or a logout) doesn't
    // bounce them back to /{group}/activate in an inescapable loop.
    localStorage.removeItem('guestUserGroup')
    // C9: validate ?to= against an allowlist. router.replace with a validated
    // relative path can never run javascript: or redirect off-origin.
    router.replace(safeRedirectPath(route.query.to) || '/m')

    //reload
    location.reload()

    return null
  }).catch(e => {
    const data = (e.response && e.response.data) || {}
    if (data.code === 'link_used') return showUsed(data.message)
    errors.value = data.errors || { password: ['Something went wrong. Please try again'] }
  })
}
const onSubmit = () => {
  refVForm.value?.validate().then(({valid: isValid}) => {
    if (isValid)
      login()
  })
}
</script>

<template>
  <div>
    <!-- Title and Logo -->
    <div class="auth-logo d-flex align-start gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />

      <!--      <h1 class="font-weight-semibold leading-normal text-2xl text-uppercase"> -->
      <!--        {{ themeConfig.app.title }} -->
      <!--      </h1> -->
    </div>

    <VRow
      class="auth-wrapper"
      no-gutters
    >
      <VCol
        lg="8"
        class="d-none d-lg-flex align-center justify-center position-relative"
      >
        <VImg
          max-width="768px"
          :src="authThemeImg"
          class="auth-illustration"
        />
        <!--        <VImg -->
        <!--          :width="276" -->
        <!--          :src="tree" -->
        <!--          class="auth-footer-start-tree" -->
        <!--        /> -->
        <VImg
          class="auth-footer-mask"
          :src="authThemeMask"
        />
      </VCol>

      <VCol
        cols="12"
        lg="4"
        class="auth-bg d-flex align-center justify-center"
      >
        <VCard
          v-if="!submitted"
          flat
          :max-width="500"
          class="mt-12 mt-sm-0 pa-4"
        >
          <VCardText
            v-if="linkState === 'checking'"
            class="text-center py-12"
          >
            <VProgressCircular
              indeterminate
              color="primary"
            />
          </VCardText>

          <VCardText v-if="linkState === 'used'">
            <p class="used-link-message mb-6">
              {{ usedMessage }}
            </p>
            <VBtn
              block
              :to="{ name: 'login' }"
            >
              Sign in
            </VBtn>
            <div class="text-center mt-4">
              <RouterLink
                class="text-primary"
                :to="{ name: 'forgot-password' }"
              >
                Reset password
              </RouterLink>
            </div>
          </VCardText>

          <VCardText v-if="linkState === 'ready'">
            <h5
              v-if="route.query.activate === 'true'"
              class="text-h5 font-weight-semibold mb-1"
            >
              Thrilled to have you as a member of RM!
            </h5>
            <br v-if="route.query.activate === 'true'">
            <h5 class="text-h5 font-weight-semibold mb-1">
              Set a New Password 🔒
            </h5>
            <p class="mb-0" />
          </VCardText>

          <VCardText v-if="linkState === 'ready'">
            <VForm
              ref="refVForm"
              @submit.prevent="onSubmit"
            >
              <VRow>
                <!-- email -->
                <VCol cols="12">
                  <VTextField
                    v-model="password"
                    label="Password"
                    :rules="[requiredValidator]"
                    :type="isPasswordVisible ? 'text' : 'password'"
                    :error-messages="errors.password"
                    :append-inner-icon="isPasswordVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                    @click:append-inner="isPasswordVisible = !isPasswordVisible"
                  />
                </VCol>

                <!-- Reset link -->
                <VCol cols="12">
                  <VBtn
                    block
                    type="submit"
                  >
                    Submit and Login
                  </VBtn>
                </VCol>

                <!-- back to login -->
                <VCol
                  cols="12"
                  class="text-center"
                >
                  <RouterLink
                    class="text-primary ms-2"
                    :to="{ name: 'login' }"
                  >
                    <VIcon
                      :rotate="isAppRtl ? '180deg' : null"
                      icon="mdi-chevron-left"
                    />
                    <span>Back to login</span>
                  </RouterLink>
                </VCol>
              </VRow>
            </VForm>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>

<style lang="scss">
@use "@core/scss/pages/page-auth.scss";
</style>

<style scoped>
.used-link-message {
  font-size: 16px;
  line-height: 1.5;
}
</style>

<route lang="yaml">
meta:
  layout: blank
  action: read
  subject: Auth
  redirectIfLoggedIn: true
</route>
